import {
  forwardRef,
  type ReactElement,
  useImperativeHandle,
  useRef,
  useState
} from "react"
import { flushSync } from "react-dom"

export type DragPreviewRenderer = (
  draggedKey: string,
  callback: (element: HTMLElement) => void
) => void

export interface DragPreviewProps {
  children: (key: string) => ReactElement
}

/*
 * An HTML5 Drag & drop preview MUST be in the document at the time setDragImage is called.
 * This component ensures it's there but invisible then immediately removed after the copy.
 */
export const DragPreview = forwardRef<DragPreviewRenderer, DragPreviewProps>(
  function DragPreview(props, ref) {
    const { children: render } = props

    const [children, setChildren] = useState<ReactElement>()
    const domRef = useRef(null)

    useImperativeHandle(
      ref,
      () => (draggedKey: string, callback: (node: HTMLElement) => void) => {
        // This will be called during onDragStart. We need to render the
        // preview synchronously before this event returns so we can call event.dataTransfer.setDragImage.
        flushSync(() => {
          setChildren(render(draggedKey))
        })

        if (domRef.current) callback(domRef.current)

        // Remove the preview from the DOM after a frame so the browser has time to paint.
        requestAnimationFrame(() => {
          setChildren(undefined)
        })
      },
      [render]
    )

    if (!children) return null

    return (
      <div
        style={{ zIndex: -100, position: "absolute", top: 0, left: -100000 }}
        ref={domRef}
      >
        {children}
      </div>
    )
  }
)
